export class DOMUtils {
  constructor(private readonly aDocument: Document) {}

  get location() {
    return this.aDocument.location
  }

  get title() {
    return this.aDocument.title
  }

  get lang() {
    return this.aDocument.documentElement.lang
  }

  get currentScript() {
    return this.aDocument.currentScript // reminder: null on deferred script
  }

  /**
   * Retrieves a URL parameters value by keys from either query string or hash fragment.
   *
   * This method searches for parameters in the following order:
   * 1. Standard query parameters (?param=value)
   * 2. Hash-based parameters (#/route?param=value) - for SPA routing
   */
  getSearchParams<S extends string>(...paramNames: S[]): Partial<Record<S, string | undefined>> {
    const getParams = (params: URLSearchParams) => {
      const tuples = paramNames.map(at => [at as S, params.get(at) ?? undefined] as const)
      const filtered = tuples.filter(([_, v]) => v !== undefined)
      return Object.fromEntries(filtered) as Partial<Record<S, string>>
    }

    // Check hash-based parameters (SPA support)
    const hash = this.location.hash.substring(1)
    if (hash.includes('?')) {
      const [, queryPart] = hash.split('?', 2)
      const hashParams = new URLSearchParams(queryPart)
      return getParams(hashParams)
    }

    return getParams(new URLSearchParams(this.location.search))
  }

  async waitForDomReady(): Promise<'headReady' | 'documentState' | 'domContentLoaded'> {
    if (document.head) {
      return 'headReady'
    }

    return new Promise(resolve => {
      if (this.aDocument.readyState === 'complete' || this.aDocument.readyState === 'interactive') {
        resolve('documentState')
      } else {
        this.aDocument.addEventListener('DOMContentLoaded', () => resolve('domContentLoaded'))
      }
    })
  }

  createScript(src: string, attributes: Record<string, string> = {}): HTMLScriptElement {
    const script = this.aDocument.createElement('script')
    script.src = src
    script.type = 'text/javascript'

    Object.entries(attributes).forEach(([key, value]) => script.setAttribute(key, value))
    return script
  }

  createIframe(src: string, hidden: boolean = true): HTMLIFrameElement {
    const iframe = this.aDocument.createElement('iframe')
    iframe.src = src
    if (hidden) {
      iframe.style.display = 'none'
    }
    return iframe
  }

  getScriptAttributes<S extends string>(srcMatcher: string, ...attributeNames: S[]): Partial<Record<S, string>> {
    const script = this.aDocument.querySelector(`script[src*="${srcMatcher}"]`)
    // reminder: this.aDocument.currentScript is null if script is async/deferred.
    if (!script) {
      throw new Error('Script element not found')
    }

    const pairs: [S, string | undefined][] = attributeNames.map(
      at => [at as S, script.getAttribute(at) ?? undefined] as const,
    )
    // TODO(roman): should support nulls?

    const filtered = pairs.filter(([_, v]) => v !== undefined)
    return Object.fromEntries(filtered) as Partial<Record<S, string>>
  }
}
